import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import {
  FiUsers,
  FiCheckCircle,
  FiClock,
  FiAward,
  FiTrendingUp,
  FiBarChart2,
  FiRefreshCw,
  FiLayers,
} from "react-icons/fi";
import { resolveWebTaskLogoUrl } from "../../api/webTaskAdminApi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RANGES = [
  { value: "today", label: "Today (Hourly)" },
  { value: 7, label: "Last 7 Days" },
  { value: 14, label: "Last 14 Days" },
  { value: 30, label: "Last 30 Days" },
];

export default function WebTaskActivityChart({
  chartData = {},
  loading = false,
  onRefresh,
  onViewTaskHistory,
}) {
  const [rangeMode, setRangeMode] = useState("today"); // "today" | 7 | 14 | 30
  const [chartType, setChartType] = useState("bar"); // "bar" | "line"

  const today = chartData?.today || {
    totalUsers: 0,
    completedTasks: 0,
    totalSessions: 0,
    totalEp: 0,
    activeSessions: 0,
    completionRate: 0,
  };

  const hourly = useMemo(() => chartData?.hourly || [], [chartData]);
  const daily = useMemo(() => chartData?.daily || [], [chartData]);
  const taskBreakdown = useMemo(() => chartData?.taskBreakdown || [], [chartData]);

  // Determine active dataset to display based on rangeMode
  const { labels, usersData, completedData, sessionsData, epData } = useMemo(() => {
    if (rangeMode === "today") {
      const items = hourly.length > 0 ? hourly : [];
      return {
        labels: items.map((i) => i.label || i.hour),
        usersData: items.map((i) => Number(i.totalUsers || 0)),
        completedData: items.map((i) => Number(i.completedTasks || 0)),
        sessionsData: items.map((i) => Number(i.totalSessions || 0)),
        epData: items.map((i) => Number(i.totalEp || 0)),
      };
    }

    const numDays = Number(rangeMode) || 7;
    const items = (daily.length > 0 ? daily : []).slice(-numDays);
    return {
      labels: items.map((i) => i.label || i.date),
      usersData: items.map((i) => Number(i.totalUsers || 0)),
      completedData: items.map((i) => Number(i.completedTasks || 0)),
      sessionsData: items.map((i) => Number(i.totalSessions || 0)),
      epData: items.map((i) => Number(i.totalEp || 0)),
    };
  }, [rangeMode, hourly, daily]);

  // Chart.js datasets & options
  const dataConfig = {
    labels: labels.length > 0 ? labels : ["No Data"],
    datasets: [
      {
        type: chartType,
        label: "Total Users",
        data: usersData.length > 0 ? usersData : [0],
        backgroundColor: chartType === "bar" ? "rgba(99, 102, 241, 0.85)" : "rgba(99, 102, 241, 0.15)",
        borderColor: "#4F46E5",
        borderWidth: chartType === "bar" ? 0 : 2.5,
        borderRadius: 6,
        fill: chartType === "line",
        tension: 0.35,
        pointBackgroundColor: "#4F46E5",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointRadius: chartType === "line" ? 4 : 0,
        pointHoverRadius: 6,
      },
      {
        type: chartType,
        label: "Completed Tasks",
        data: completedData.length > 0 ? completedData : [0],
        backgroundColor: chartType === "bar" ? "rgba(16, 185, 129, 0.85)" : "rgba(16, 185, 129, 0.15)",
        borderColor: "#10B981",
        borderWidth: chartType === "bar" ? 0 : 2.5,
        borderRadius: 6,
        fill: chartType === "line",
        tension: 0.35,
        pointBackgroundColor: "#10B981",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointRadius: chartType === "line" ? 4 : 0,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          padding: 16,
          font: { size: 12, weight: "bold", family: "system-ui, -apple-system" },
          color: "#4B5563",
        },
      },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 10,
        boxPadding: 6,
        callbacks: {
          footer: (tooltipItems) => {
            const idx = tooltipItems[0]?.dataIndex;
            if (idx != null && sessionsData[idx] != null) {
              const sess = sessionsData[idx];
              const ep = epData[idx];
              return `Total Attempts: ${sess} | EP Rewarded: ${ep} EP`;
            }
            return "";
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11, weight: "500" },
          color: "#9CA3AF",
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: rangeMode === "today" ? 12 : 14,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(243, 244, 246, 1)" },
        ticks: {
          stepSize: 1,
          font: { size: 11, weight: "500" },
          color: "#9CA3AF",
          precision: 0,
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* 1. TOP KPI SUMMARY CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Today's Total Users */}
        <div className="bg-white rounded-2xl shadow-xs border border-indigo-100 p-5 flex flex-col justify-between hover:border-indigo-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              Today's Total Users
            </span>
            <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
              <FiUsers size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-black text-indigo-700 font-mono">
              {Number(today.totalUsers || 0).toLocaleString()}
            </p>
            <p className="text-xs text-indigo-600/80 font-medium mt-1 flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
              Active players today
            </p>
          </div>
        </div>

        {/* Card 2: Today's Completed Tasks */}
        <div className="bg-white rounded-2xl shadow-xs border border-emerald-100 p-5 flex flex-col justify-between hover:border-emerald-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              Today's Completed Tasks
            </span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <FiCheckCircle size={16} />
            </div>
          </div>
          <div className="mt-2">
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-black text-emerald-700 font-mono">
                {Number(today.completedTasks || 0).toLocaleString()}
              </p>
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                {today.completionRate || 0}% rate
              </span>
            </div>
            <p className="text-xs text-emerald-600/80 font-medium mt-1">
              Successfully finished sessions
            </p>
          </div>
        </div>

        {/* Card 3: Today's Total Attempts */}
        <div className="bg-white rounded-2xl shadow-xs border border-amber-100 p-5 flex flex-col justify-between hover:border-amber-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              Today's Total Attempts
            </span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <FiClock size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-black text-amber-700 font-mono">
              {Number(today.totalSessions || 0).toLocaleString()}
            </p>
            <p className="text-xs text-amber-700/80 font-medium mt-1">
              {today.activeSessions > 0
                ? `${today.activeSessions} in-progress sessions`
                : "Total sessions initialized today"}
            </p>
          </div>
        </div>

        {/* Card 4: Today's EP Awarded */}
        <div className="bg-white rounded-2xl shadow-xs border border-purple-100 p-5 flex flex-col justify-between hover:border-purple-300 transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              Today's EP Awarded
            </span>
            <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold">
              <FiAward size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-3xl font-black text-purple-700 font-mono">
              {Number(today.totalEp || 0).toLocaleString()}
            </p>
            <p className="text-xs text-purple-600/80 font-medium mt-1">
              Event Points credited to users
            </p>
          </div>
        </div>
      </div>

      {/* 2. MAIN CHART MODULE */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6 space-y-4">
        {/* Controls Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <h3 className="text-base font-black text-gray-900">
                Today's Activity: Total Users &amp; Completed Tasks
              </h3>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              Live side-by-side comparison of active users and completed tasks in a single interactive chart
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Range Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
              {RANGES.map((r) => (
                <button
                  key={String(r.value)}
                  type="button"
                  onClick={() => setRangeMode(r.value)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                    rangeMode === r.value
                      ? "bg-white text-black shadow-xs"
                      : "text-gray-500 hover:text-black"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Chart Type Toggle */}
            <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
              <button
                type="button"
                onClick={() => setChartType("bar")}
                className={`p-1.5 rounded-lg transition cursor-pointer ${
                  chartType === "bar"
                    ? "bg-white text-black shadow-xs"
                    : "text-gray-400 hover:text-black"
                }`}
                title="Bar Chart"
              >
                <FiBarChart2 size={15} />
              </button>
              <button
                type="button"
                onClick={() => setChartType("line")}
                className={`p-1.5 rounded-lg transition cursor-pointer ${
                  chartType === "line"
                    ? "bg-white text-black shadow-xs"
                    : "text-gray-400 hover:text-black"
                }`}
                title="Line Chart"
              >
                <FiTrendingUp size={15} />
              </button>
            </div>

            {/* Refresh Button */}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:text-black hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
                title="Refresh Analytics"
              >
                <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
            )}
          </div>
        </div>

        {/* Chart Canvas */}
        <div className="h-80 w-full relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : null}
          {chartType === "bar" ? (
            <Bar data={dataConfig} options={chartOptions} />
          ) : (
            <Line data={dataConfig} options={chartOptions} />
          )}
        </div>

        {/* Legend Footnote */}
        <div className="flex flex-wrap items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-indigo-600 inline-block" />
              <b className="text-gray-700">Total Users:</b> Distinct participants who began a web task
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />
              <b className="text-gray-700">Completed Tasks:</b> Sessions that fulfilled reward tiers and claimed EP
            </span>
          </div>
          <span>
            {rangeMode === "today"
              ? "Viewing Today's hourly activity (24-hour cycle)"
              : `Viewing daily activity over the last ${rangeMode} days`}
          </span>
        </div>
      </div>

      {/* 3. TODAY'S PER-TASK BREAKDOWN TABLE */}
      {taskBreakdown.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-black text-gray-900 flex items-center gap-2">
                <FiLayers size={16} />
                <span>Today's Task Performance Breakdown</span>
              </h4>
              <p className="text-xs text-gray-400 mt-0.5">
                Detailed metrics for each partner website task for today
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-[11px] text-gray-500 uppercase font-bold tracking-wider">
                  <th className="px-4 py-3">Task Name</th>
                  <th className="px-4 py-3">Tag</th>
                  <th className="px-4 py-3 text-center">Today's Users</th>
                  <th className="px-4 py-3 text-center">Today's Completed</th>
                  <th className="px-4 py-3 text-center">Completion Rate</th>
                  <th className="px-4 py-3 text-right">Today's EP Awarded</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium">
                {taskBreakdown.map((t) => {
                  const logoSrc = resolveWebTaskLogoUrl(t.logoUrl);
                  return (
                    <tr key={t.id || t.taskId} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {logoSrc ? (
                            <img
                              src={logoSrc}
                              alt=""
                              className="w-7 h-7 rounded-lg object-contain bg-gray-50 border border-gray-200"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-[10px]">
                              {t.title ? t.title[0] : "T"}
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-gray-900">{t.title}</div>
                            <div className="text-[10px] font-mono text-gray-400">{t.taskId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-block text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded text-black"
                          style={{
                            backgroundColor: `${t.tagColor || "#FFB300"}40`,
                            border: `1px solid ${t.tagColor || "#FFB300"}`,
                          }}
                        >
                          {t.tag || "GAMES"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-mono font-bold text-indigo-700">
                        {t.todayUsers.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center font-mono font-bold text-emerald-700">
                        {t.todayCompleted.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-1.5">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-emerald-500 h-1.5 rounded-full"
                              style={{ width: `${Math.min(t.completionRate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="font-mono text-[11px] text-gray-600">
                            {t.completionRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-black text-purple-700">
                        +{t.todayEp.toLocaleString()} EP
                      </td>
                      <td className="px-4 py-3 text-right">
                        {onViewTaskHistory && (
                          <button
                            type="button"
                            onClick={() => onViewTaskHistory(t.taskId)}
                            className="text-xs text-blue-600 hover:text-black font-semibold underline cursor-pointer"
                          >
                            View History
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

